import { useMemo } from "react";
import PropTypes from "prop-types";

// @mui material components
import Grid from "@mui/material/Grid";

// Material Dashboard 2 React components
import MDInput from "components/MDInput";

import ChonDiaChi from "components/ChonDiaChi";
import ChonNganHang from "components/ChonNganHang";
import NhapNgay from "components/NhapNgay";
import TieuDeNhomForm, { coNoiDung, useNhomFormMo } from "components/TieuDeNhomForm";
import { noiCapCccdTheoNgay } from "lib/thongTinCaNhanChuVuon";
import { LOAI_CHU_VUON_THUONG, NHAN_LOAI_CHU_VUON } from "lib/quyTacMaChuVuon";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const THU_TU_NHOM = ["dinhDanh", "giayTo", "lienHe", "noiCuTru", "nganHang", "ghiChu"];
const NHOM_TUY_CHON = ["ghiChu"];

/** Nhóm bắt buộc: true/false. Khi không ép cá nhân → coi như đủ (xanh). */
function trangThaiBatBuoc(duDieuKien, batBuoc) {
  if (!batBuoc) return true;
  return duDieuKien;
}

// Form đầy đủ thông tin Chủ vườn — dùng chung Quản lý (admin) và thu mua hiện trường.
// Tạo mới / admin sửa: khối cá nhân + tài khoản nhận tiền bắt buộc.
// NV đề xuất sửa (batBuocCaNhan=false): không ép đủ field cũ.
// Ghi chú không bắt buộc. Mỗi nhóm đóng/mở — chỉ mở 1 nhóm để nhập tập trung.
function FormChuVuon({ value, onChange, khoaCccd, batBuocCaNhan }) {
  const set = (patch) => onChange({ ...value, ...patch });
  const noiCapHienThi = noiCapCccdTheoNgay(value.ngayCapCccd);

  const okDinhDanh = trangThaiBatBuoc(
    coNoiDung(value.loaiChuVuon ?? LOAI_CHU_VUON_THUONG) &&
      coNoiDung(value.hoTen) &&
      coNoiDung(value.ngaySinh),
    batBuocCaNhan
  );
  const okGiayTo = trangThaiBatBuoc(
    coNoiDung(value.cccd) && coNoiDung(value.ngayCapCccd) && coNoiDung(noiCapHienThi),
    batBuocCaNhan
  );
  const okLienHe = trangThaiBatBuoc(
    coNoiDung(value.sdt) && EMAIL_RE.test(String(value.email ?? "").trim()),
    batBuocCaNhan
  );
  const okNoiCuTru = trangThaiBatBuoc(
    (coNoiDung(value.tinhMa) || coNoiDung(value.tinh)) &&
      (coNoiDung(value.xaMa) || coNoiDung(value.xa)) &&
      coNoiDung(value.thon) &&
      coNoiDung(value.diaChi),
    batBuocCaNhan
  );
  const okNganHang = trangThaiBatBuoc(
    coNoiDung(value.tenNganHang) && coNoiDung(value.soTkNganHang) && coNoiDung(value.chuTaiKhoan),
    batBuocCaNhan
  );
  // Ghi chú: đủ sau khi đóng nhóm bằng Divider (nhomTuyChon).
  const okGhiChuPlaceholder = false;

  const trangThai = useMemo(
    () => ({
      dinhDanh: okDinhDanh,
      giayTo: okGiayTo,
      lienHe: okLienHe,
      noiCuTru: okNoiCuTru,
      nganHang: okNganHang,
      ghiChu: okGhiChuPlaceholder,
    }),
    [okDinhDanh, okGiayTo, okLienHe, okNoiCuTru, okNganHang]
  );

  const { nhomMo, toggleNhom, khoaNhom, trangThaiHieuLuc } = useNhomFormMo(
    THU_TU_NHOM,
    trangThai,
    NHOM_TUY_CHON,
    Boolean(value.id || value.maChuVuon)
  );

  return (
    <>
      <TieuDeNhomForm
        tieuDe="1. Định danh"
        moTa="Họ tên theo CCCD. Loại chủ vườn dùng để sinh mã."
        hoanThanh={okDinhDanh}
        mo={nhomMo === "dinhDanh"}
        khoa={khoaNhom("dinhDanh")}
        onToggle={() => toggleNhom("dinhDanh")}
      >
        <Grid item xs={12} sm={6}>
          <MDInput
            select
            SelectProps={{ native: true }}
            label="Loại chủ vườn"
            value={value.loaiChuVuon ?? LOAI_CHU_VUON_THUONG}
            onChange={(e) => set({ loaiChuVuon: e.target.value })}
            fullWidth
            required={!khoaCccd}
            disabled={khoaCccd}
            InputLabelProps={{ shrink: true }}
          >
            {Object.entries(NHAN_LOAI_CHU_VUON).map(([gt, nhan]) => (
              <option key={gt} value={gt}>
                {nhan}
              </option>
            ))}
          </MDInput>
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDInput
            label="Mã chủ vườn"
            value={value.maChuVuon || "Sẽ sinh sau khi lưu"}
            fullWidth
            disabled
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={8}>
          <MDInput
            label="Họ và tên"
            value={value.hoTen}
            onChange={(e) => set({ hoTen: e.target.value })}
            fullWidth
            required={batBuocCaNhan}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <NhapNgay
            label="Ngày sinh"
            value={value.ngaySinh ?? ""}
            onChange={(ngaySinh) => set({ ngaySinh })}
            required={batBuocCaNhan}
          />
        </Grid>
      </TieuDeNhomForm>

      <TieuDeNhomForm
        tieuDe="2. Giấy tờ tùy thân"
        moTa="Nơi cấp điền tự động theo ngày cấp CCCD — không nhập tay."
        hoanThanh={okGiayTo}
        mo={nhomMo === "giayTo"}
        khoa={khoaNhom("giayTo")}
        onToggle={() => toggleNhom("giayTo")}
      >
        <Grid item xs={12} sm={6}>
          <MDInput
            label={
              khoaCccd && !value.cccdDayDu && String(value.cccd ?? "").includes("*")
                ? "Số CCCD (đã mã hóa)"
                : "Số CCCD"
            }
            value={
              khoaCccd
                ? value.cccdDayDu || value.cccd
                : value.id && String(value.cccd ?? "").includes("*")
                ? value.cccdDayDu || ""
                : value.cccd
            }
            onChange={(e) => set({ cccd: e.target.value })}
            disabled={khoaCccd}
            fullWidth
            required={batBuocCaNhan}
            helperText={
              khoaCccd
                ? "Số đủ dùng cho bảng kê thuế (Biểu 01)."
                : value.id
                ? "Nhập số CCCD đủ — bắt buộc để kê khai thuế."
                : "Nhân viên thấy dạng che; số đủ lưu để kê khai thuế."
            }
            InputLabelProps={
              khoaCccd && String(value.cccdDayDu || value.cccd || "").length > 0
                ? { shrink: true }
                : undefined
            }
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <NhapNgay
            label="Ngày cấp CCCD"
            value={value.ngayCapCccd ?? ""}
            onChange={(ngayCapCccd) =>
              set({ ngayCapCccd, noiCapCccd: noiCapCccdTheoNgay(ngayCapCccd) })
            }
            required={batBuocCaNhan}
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label="Nơi cấp CCCD"
            value={noiCapHienThi}
            fullWidth
            disabled
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
      </TieuDeNhomForm>

      <TieuDeNhomForm
        tieuDe="3. Liên hệ"
        hoanThanh={okLienHe}
        mo={nhomMo === "lienHe"}
        khoa={khoaNhom("lienHe")}
        onToggle={() => toggleNhom("lienHe")}
      >
        <Grid item xs={12} sm={6}>
          <MDInput
            label="Số điện thoại"
            value={value.sdt}
            onChange={(e) => set({ sdt: e.target.value })}
            fullWidth
            required={batBuocCaNhan}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDInput
            type="email"
            label="Email"
            value={value.email ?? ""}
            onChange={(e) => set({ email: e.target.value })}
            fullWidth
            required={batBuocCaNhan}
          />
        </Grid>
      </TieuDeNhomForm>

      <TieuDeNhomForm
        tieuDe="4. Nơi cư trú"
        moTa="Tỉnh → xã → thôn → số nhà / đường."
        hoanThanh={okNoiCuTru}
        mo={nhomMo === "noiCuTru"}
        khoa={khoaNhom("noiCuTru")}
        onToggle={() => toggleNhom("noiCuTru")}
      >
        <ChonDiaChi
          tinh={value.tinh ?? ""}
          xa={value.xa ?? ""}
          onChange={({ tinh, tinhMa, xa, xaMa }) => set({ tinh, tinhMa, xa, xaMa })}
          batBuoc={batBuocCaNhan}
        />
        <Grid item xs={12} sm={6}>
          <MDInput
            label="Thôn/Ấp"
            value={value.thon ?? ""}
            onChange={(e) => set({ thon: e.target.value })}
            fullWidth
            required={batBuocCaNhan}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDInput
            label="Số nhà / Đường"
            value={value.diaChi ?? ""}
            onChange={(e) => set({ diaChi: e.target.value })}
            fullWidth
            required={batBuocCaNhan}
          />
        </Grid>
      </TieuDeNhomForm>

      <TieuDeNhomForm
        tieuDe="5. Tài khoản nhận tiền"
        moTa="Bắt buộc — dùng khi thanh toán chuyển khoản."
        hoanThanh={okNganHang}
        mo={nhomMo === "nganHang"}
        khoa={khoaNhom("nganHang")}
        onToggle={() => toggleNhom("nganHang")}
      >
        <Grid item xs={12} sm={6}>
          <ChonNganHang
            value={value.tenNganHang ?? ""}
            onChange={(tenNganHang) => set({ tenNganHang })}
            required={batBuocCaNhan}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDInput
            label="Số tài khoản"
            value={value.soTkNganHang ?? ""}
            onChange={(e) => set({ soTkNganHang: e.target.value })}
            fullWidth
            required={batBuocCaNhan}
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label="Tên chủ tài khoản"
            value={value.chuTaiKhoan ?? ""}
            onChange={(e) => set({ chuTaiKhoan: e.target.value })}
            fullWidth
            required={batBuocCaNhan}
          />
        </Grid>
      </TieuDeNhomForm>

      <TieuDeNhomForm
        tieuDe="6. Ghi chú"
        moTa="Không bắt buộc — bấm Divider để đóng rồi mở mục khác."
        hoanThanh={trangThaiHieuLuc.ghiChu}
        mo={nhomMo === "ghiChu"}
        khoa={khoaNhom("ghiChu")}
        onToggle={() => toggleNhom("ghiChu")}
      >
        <Grid item xs={12}>
          <MDInput
            label="Ghi chú"
            value={value.ghiChu ?? ""}
            onChange={(e) => set({ ghiChu: e.target.value })}
            fullWidth
            multiline
            rows={2}
          />
        </Grid>
      </TieuDeNhomForm>
    </>
  );
}

FormChuVuon.defaultProps = {
  khoaCccd: false,
  batBuocCaNhan: true,
};

FormChuVuon.propTypes = {
  value: PropTypes.shape({
    id: PropTypes.string,
    maChuVuon: PropTypes.string,
    hoTen: PropTypes.string,
    ngaySinh: PropTypes.string,
    cccd: PropTypes.string,
    cccdDayDu: PropTypes.string,
    sdt: PropTypes.string,
    loaiChuVuon: PropTypes.string,
    ngayCapCccd: PropTypes.string,
    noiCapCccd: PropTypes.string,
    email: PropTypes.string,
    diaChi: PropTypes.string,
    tinh: PropTypes.string,
    tinhMa: PropTypes.string,
    xa: PropTypes.string,
    xaMa: PropTypes.string,
    thon: PropTypes.string,
    soTkNganHang: PropTypes.string,
    tenNganHang: PropTypes.string,
    chuTaiKhoan: PropTypes.string,
    ghiChu: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  khoaCccd: PropTypes.bool,
  batBuocCaNhan: PropTypes.bool,
};

export default FormChuVuon;

export const RONG_CHU_VUON = {
  hoTen: "",
  ngaySinh: "",
  cccd: "",
  sdt: "",
  loaiChuVuon: LOAI_CHU_VUON_THUONG,
  ngayCapCccd: "",
  noiCapCccd: "",
  email: "",
  diaChi: "",
  tinh: "",
  tinhMa: "",
  xa: "",
  xaMa: "",
  thon: "",
  soTkNganHang: "",
  tenNganHang: "",
  chuTaiKhoan: "",
  ghiChu: "",
};
